import React, { useCallback } from 'react';

import { useCommonTranslation } from '../../../../hooks/useCommonTranslation';
import { useAppDispatch } from '../../../../hooks/useAppDispatch';
import { useAppSelector } from '../../../../hooks/useAppSelector';
import {
  useActivateDialog,
  useConfirmDialog,
} from '../../../../hooks/useConfirmDialog';

import { Client } from '../../../../data/models/client.model';
import {
  Program,
  ServicePlan,
  ServicePlanServiceArea,
} from '../../../../data/models/client-program.model';
import { TransportationBenefit } from '../../../../data/models/transportation-benefit.model';
import { ClientPurposeCode } from '../../../../data/models/purpose-code-model.model';
import * as clientSlice from '../../../../data/client.slice';

import ClientPrograms from '../ClientPrograms';
import ClientServicePlans from '../ClientServicePlans';
import ClientPurposeCodes from '../ClientPurposeCodes';
import ClientServiceAreasDeprecated from '../ClientServiceAreasDeprecated';
import ClientTransportationBenefits from '../transportation-benefit/ClientTransportationBenefits';
import ProgramDialog from '../ProgramDialog';
import ServicePlanDialog from '../ServicePlanDialog';
import PurposeCodeDialog from '../PurposeCodeDialog';
import ServiceAreaDialog from '../ServiceAreaDialog';
import TransportationBenefitDialog from '../transportation-benefit/TransportationBenefitDialog';
import ConfirmationDialog from '../../../ConfirmationDialog';
import ActivateDialog from '../../ActivateDialog';

type ServicePlanSelections = { [id: string]: boolean };

interface ClientProgramsTabProps {
  client?: Client;
  servicePlanSelections: ServicePlanSelections;
  setServicePlanSelections: (sels: ServicePlanSelections) => void;
}

/**
 * Programs tab of ClientDetails. Owns the drag/drop state so drag events
 * re-render this tab only, not the whole details page.
 */
export default function ClientProgramsTab({
  client,
  servicePlanSelections,
  setServicePlanSelections,
}: ClientProgramsTabProps) {
  const t = useCommonTranslation();
  const dispatch = useAppDispatch();

  const purposeCodes = useAppSelector(clientSlice.selectPurposeCodes);
  const serviceAreas = useAppSelector(clientSlice.selectServiceAreas);
  const transportationBenefits = useAppSelector(
    clientSlice.selectTransportationBenefits,
  );
  const servicePlanList = useAppSelector(clientSlice.selectServicePlanList);
  const programs = useAppSelector(clientSlice.selectPrograms);

  const [editingServiceArea, setEditingServiceArea] =
    React.useState<ServicePlanServiceArea>();
  const [editingServicePlan, setEditingServicePlan] =
    React.useState<ServicePlan>();
  const [editingProgram, setEditingProgram] = React.useState<Program>();
  const [editingTransportationBenefit, setEditingTransportationBenefit] =
    React.useState<TransportationBenefit>();
  const [purposeCodeDialogOpen, setPurposeCodeDialogOpen] =
    React.useState(false);
  const [serviceAreaDialogOpen, setServiceAreaDialogOpen] =
    React.useState(false);
  const [transportationBenefitDialogOpen, setTransportationBenefitDialogOpen] =
    React.useState(false);
  const [servicePlanDialogOpen, setServicePlanDialogOpen] =
    React.useState(false);
  const [programDialogOpen, setProgramDialogOpen] = React.useState(false);
  const confirmDialog = useConfirmDialog();
  const activateDialog = useActivateDialog();

  const [draggedPurposeCode, setDraggedPurposeCode] =
    React.useState<ClientPurposeCode>();
  const [draggedServiceArea, setDraggedServiceArea] =
    React.useState<ServicePlanServiceArea>();
  const [draggedTransportationBenefit, setDraggedTransportationBenefit] =
    React.useState<TransportationBenefit>();
  const [draggedServicePlan, setDraggedServicePlan] =
    React.useState<ServicePlan>();
  const [droppedServicePlans, setDroppedServicePlans] = React.useState<
    ServicePlan[]
  >([]);
  const [droppedProgram, setDroppedProgram] = React.useState<Program>();

  const fetchMoreServicePlans = (token: string) => {
    dispatch(
      clientSlice.fetchClientServicePlansAsync(client?.clientBC!, token),
    );
  };

  // Purpose codes

  const addPurposeCode = () => {
    setPurposeCodeDialogOpen(true);
  };

  const activatePurposeCode = (purposeCode: ClientPurposeCode) => {
    confirmDialog.request({
      title: t('client.confirmActivatePurposeCodeTitle'),
      message: t('client.confirmActivatePurposeCodeMessage'),
      onConfirm: () =>
        dispatch(
          clientSlice.activateClientPurposeCodeAsync(
            client!.clientBC,
            purposeCode,
          ),
        ),
    });
  };

  const deactivatePurposeCode = (purposeCode: ClientPurposeCode) => {
    confirmDialog.request({
      title: t('client.confirmDeactivatePurposeCodeTitle'),
      message: t('client.confirmDeactivatePurposeCodeMessage'),
      onConfirm: () =>
        dispatch(
          clientSlice.deactivateClientPurposeCodeAsync(
            client!.clientBC,
            purposeCode,
          ),
        ),
    });
  };

  const deletePurposeCode = (purposeCode: ClientPurposeCode) => {
    confirmDialog.request({
      title: t('client.confirmDeletePurposeCodeTitle'),
      message: t('client.confirmDeletePurposeCodeMessage'),
      onConfirm: () =>
        dispatch(
          clientSlice.deleteClientPurposeCodeAsync(
            client!.clientBC,
            purposeCode,
          ),
        ),
    });
  };

  // Service areas

  const addServiceArea = () => {
    setEditingServiceArea(undefined);
    setServiceAreaDialogOpen(true);
  };

  const editServiceArea = (serviceArea: ServicePlanServiceArea) => {
    setEditingServiceArea(serviceArea);
    setServiceAreaDialogOpen(true);
  };

  const activateServiceArea = (serviceArea: ServicePlanServiceArea) => {
    confirmDialog.request({
      title: t('client.confirmActivateServiceAreaTitle'),
      message: t('client.confirmActivateServiceAreaMessage'),
      onConfirm: () =>
        dispatch(
          clientSlice.activateClientServiceAreaAsync(
            client!.clientBC,
            serviceArea,
          ),
        ),
    });
  };

  const deactivateServiceArea = (serviceArea: ServicePlanServiceArea) => {
    confirmDialog.request({
      title: t('client.confirmDeactivateServiceAreaTitle'),
      message: t('client.confirmDeactivateServiceAreaMessage'),
      onConfirm: () =>
        dispatch(
          clientSlice.deactivateClientServiceAreaAsync(
            client!.clientBC,
            serviceArea,
          ),
        ),
    });
  };

  const deleteServiceArea = (serviceArea: ServicePlanServiceArea) => {
    confirmDialog.request({
      title: t('client.confirmDeleteServiceAreaTitle'),
      message: t('client.confirmDeleteServiceAreaMessage'),
      onConfirm: () =>
        dispatch(
          clientSlice.deleteClientServiceAreaAsync(
            client!.clientBC,
            serviceArea,
          ),
        ),
    });
  };

  // Transportation benefits

  const addTransportationBenefit = () => {
    setEditingTransportationBenefit(undefined);
    setTransportationBenefitDialogOpen(true);
  };

  const editTransportationBenefit = (
    evt: any,
    transportationBenefit: TransportationBenefit,
  ) => {
    evt.preventDefault();
    evt.stopPropagation();
    setEditingTransportationBenefit(transportationBenefit);
    setTransportationBenefitDialogOpen(true);
  };

  const activateTransportationBenefit = (
    evt: any,
    transportationBenefit: TransportationBenefit,
  ) => {
    evt.preventDefault();
    evt.stopPropagation();
    activateDialog.request({
      title: t('client.setTransportationBenefitDatesTitle'),
      message: t('client.setTransportationBenefitDatesMessage'),
      forActivate: true,
      onConfirm: (start, end) =>
        dispatch(
          clientSlice.updateTransportationBenefitEffectiveDatesAsync(
            client!.clientBC,
            transportationBenefit.transportationBenefitUUID!,
            start,
            end,
          ),
        ),
    });
  };

  // Deactivating a transportation benefit is intentionally disabled.
  const deactivateTransportationBenefit = (
    _evt: any,
    _transportationBenefit: TransportationBenefit,
  ) => {};

  const deleteTransportationBenefit = (
    evt: any,
    transportationBenefit: TransportationBenefit,
  ) => {
    evt.preventDefault();
    evt.stopPropagation();
    confirmDialog.request({
      title: t('client.confirmDeleteTransportationBenefitTitle'),
      message: t('client.confirmDeleteTransportationBenefitMessage'),
      onConfirm: () =>
        dispatch(
          clientSlice.deleteTransportationBenefitAsync(
            client!.clientBC,
            transportationBenefit,
          ),
        ),
    });
  };

  // Service plans

  const addServicePlan = () => {
    setEditingServicePlan(undefined);
    setServicePlanDialogOpen(true);
  };

  const editServicePlan = (evt: any, servicePlan: ServicePlan) => {
    evt.preventDefault();
    evt.stopPropagation();
    setEditingServicePlan(servicePlan);
    setServicePlanDialogOpen(true);
  };

  const requestServicePlanDates = (
    evt: any,
    servicePlan: ServicePlan,
    forActivate: boolean,
  ) => {
    evt.preventDefault();
    evt.stopPropagation();
    activateDialog.request({
      title: forActivate
        ? t('client.confirmActivateServicePlanTitle')
        : t('client.confirmDeactivateServicePlanTitle'),
      message: forActivate
        ? t('client.confirmActivateServicePlanMessage')
        : t('client.confirmDeactivateServicePlanMessage'),
      forActivate,
      onConfirm: (start, end) =>
        dispatch(
          clientSlice.updateServicePlanEffectiveDatesAsync(
            client!.clientBC,
            servicePlan.servicePlanUUID!,
            start,
            end,
          ),
        ),
    });
  };

  const activateServicePlan = (evt: any, servicePlan: ServicePlan) =>
    requestServicePlanDates(evt, servicePlan, true);

  const deactivateServicePlan = (evt: any, servicePlan: ServicePlan) =>
    requestServicePlanDates(evt, servicePlan, false);

  const deleteServicePlan = (evt: any, servicePlan: ServicePlan) => {
    evt.preventDefault();
    evt.stopPropagation();
    confirmDialog.request({
      title: t('client.confirmDeleteServicePlanTitle'),
      message: t('client.confirmDeleteServicePlanMessage'),
      onConfirm: () =>
        dispatch(
          clientSlice.deleteClientServicePlanAsync(
            client!.clientBC,
            servicePlan,
          ),
        ),
    });
  };

  const unassignPurposeCode = (
    servicePlan: ServicePlan,
    purposeCode: ClientPurposeCode,
  ) => {
    confirmDialog.request({
      title: t('client.confirmUnassignPurposeCodeTitle'),
      message: t('client.confirmUnassignPurposeCodeMessage'),
      onConfirm: () =>
        dispatch(
          clientSlice.unassignClientPurposeCodeAsync(
            client!.clientBC,
            servicePlan.servicePlanUUID!,
            purposeCode.purposeCodeUUID!,
          ),
        ),
    });
  };

  const unassignServiceArea = (
    servicePlan: ServicePlan,
    serviceArea: ServicePlanServiceArea,
  ) => {
    confirmDialog.request({
      title: t('client.confirmUnassignServiceAreaTitle'),
      message: t('client.confirmUnassignServiceAreaMessage'),
      onConfirm: () =>
        dispatch(
          clientSlice.unassignClientServiceAreaAsync(
            client!.clientBC,
            servicePlan.servicePlanUUID!,
            serviceArea.serviceAreaUUID!,
          ),
        ),
    });
  };

  const unassignTransportationBenefit = (
    servicePlan: ServicePlan,
    transportationBenefit: TransportationBenefit,
  ) => {
    confirmDialog.request({
      title: t('client.confirmUnassignTransportationBenefitTitle'),
      message: t('client.confirmUnassignTransportationBenefitMessage'),
      onConfirm: () =>
        dispatch(
          clientSlice.unassignTransportationBenefitAsync(
            client!.clientBC,
            servicePlan.servicePlanUUID!,
            transportationBenefit.transportationBenefitUUID!,
          ),
        ),
    });
  };

  // Programs

  const addProgram = () => {
    setEditingProgram(undefined);
    setProgramDialogOpen(true);
  };

  const editProgram = (evt: any, program: Program) => {
    evt.preventDefault();
    evt.stopPropagation();
    setEditingProgram(program);
    setProgramDialogOpen(true);
  };

  const requestProgramDates = (
    evt: any,
    program: Program,
    forActivate: boolean,
  ) => {
    evt.preventDefault();
    evt.stopPropagation();
    activateDialog.request({
      title: forActivate
        ? t('client.confirmActivateProgramTitle')
        : t('client.confirmDeactivateProgramTitle'),
      message: forActivate
        ? t('client.confirmActivateProgramMessage')
        : t('client.confirmDeactivateProgramMessage'),
      forActivate,
      onConfirm: (start, end) =>
        dispatch(
          clientSlice.updateProgramEffectiveDatesAsync(
            client!.clientBC,
            program.programUUID!,
            start,
            end,
          ),
        ),
    });
  };

  const activateProgram = (evt: any, program: Program) =>
    requestProgramDates(evt, program, true);

  const deactivateProgram = (evt: any, program: Program) =>
    requestProgramDates(evt, program, false);

  const deleteProgram = (evt: any, program: Program) => {
    evt.preventDefault();
    evt.stopPropagation();
    confirmDialog.request({
      title: t('client.confirmDeleteProgramTitle'),
      message: t('client.confirmDeleteProgramMessage'),
      onConfirm: () =>
        dispatch(
          clientSlice.deleteClientProgramAsync(client!.clientBC, program),
        ),
    });
  };

  // Drag and drop

  const onStartDragPurposeCode = useCallback(
    (purposeCode: ClientPurposeCode) => setDraggedPurposeCode(purposeCode),
    [],
  );
  const onEndDragPurposeCode = useCallback(
    () => setDraggedPurposeCode(undefined),
    [],
  );
  const onStartDragServiceArea = useCallback(
    (serviceArea: ServicePlanServiceArea) => setDraggedServiceArea(serviceArea),
    [],
  );
  const onEndDragServiceArea = useCallback(
    () => setDraggedServiceArea(undefined),
    [],
  );
  const onStartDragTransportationBenefit = useCallback(
    (transportationBenefit: TransportationBenefit) =>
      setDraggedTransportationBenefit(transportationBenefit),
    [],
  );
  const onEndDragTransportationBenefit = useCallback(
    () => setDraggedTransportationBenefit(undefined),
    [],
  );
  const onStartDragServicePlan = useCallback(
    (servicePlan: ServicePlan) => setDraggedServicePlan(servicePlan),
    [],
  );
  const onEndDragServicePlan = useCallback(
    () => setDraggedServicePlan(undefined),
    [],
  );

  const isDraggingAssignable = !!(
    draggedPurposeCode ||
    draggedServiceArea ||
    draggedTransportationBenefit
  );

  const getSelectedServicePlans = useCallback(
    (): ServicePlan[] =>
      (servicePlanList?.servicePlans ?? []).filter(
        (sp) => servicePlanSelections[sp.servicePlanUUID!],
      ),
    [servicePlanList, servicePlanSelections],
  );

  const onDragEnterServicePlan = useCallback(
    (evt: any) => {
      if (isDraggingAssignable) {
        evt.preventDefault();
        setDroppedServicePlans(getSelectedServicePlans());
      }
    },
    [isDraggingAssignable, getSelectedServicePlans],
  );

  const onDragLeaveServicePlan = useCallback(() => {
    setDroppedServicePlans([]);
  }, []);

  const onDropOnServicePlan = useCallback(
    (servicePlan: ServicePlan) => {
      const servicePlans = getSelectedServicePlans();
      if (!servicePlans.length) {
        return;
      }
      setDroppedServicePlans([]);
      if (!client || !servicePlan) {
        return;
      }
      const assign = (thunk: Function, uuid: string) => {
        servicePlans.forEach((sp) => {
          dispatch(thunk(client.clientBC, uuid, sp.servicePlanUUID));
        });
      };
      if (draggedPurposeCode) {
        assign(
          clientSlice.assignClientPurposeCodeAsync,
          draggedPurposeCode.purposeCodeUUID!,
        );
      } else if (draggedServiceArea) {
        assign(
          clientSlice.assignClientServiceAreaAsync,
          draggedServiceArea.serviceAreaUUID!,
        );
      } else if (draggedTransportationBenefit) {
        assign(
          clientSlice.assignTransportationBenefitAsync,
          draggedTransportationBenefit.transportationBenefitUUID!,
        );
      }
    },
    [
      client,
      dispatch,
      getSelectedServicePlans,
      draggedPurposeCode,
      draggedServiceArea,
      draggedTransportationBenefit,
    ],
  );

  const onDragEnterProgram = useCallback(
    (evt: any, program: Program) => {
      const droppable =
        program.state === 'draft' || program.state === 'inactive';
      if (draggedServicePlan && droppable) {
        evt.preventDefault();
        setDroppedProgram(program);
      }
    },
    [draggedServicePlan],
  );

  const onDragLeaveProgram = useCallback(() => {
    setDroppedProgram(undefined);
  }, []);

  const onDropOnProgram = useCallback(
    (program: Program) => {
      setDroppedProgram(undefined);
      if (client && draggedServicePlan && program) {
        dispatch(
          clientSlice.reassignClientServicePlanAsync(
            client.clientBC!,
            draggedServicePlan.servicePlanUUID!,
            program.programUUID!,
          ),
        );
      }
    },
    [client, dispatch, draggedServicePlan],
  );

  return (
    <>
      <ClientPurposeCodes
        client={client}
        purposeCodes={purposeCodes}
        onAdd={addPurposeCode}
        onActivate={activatePurposeCode}
        onDeactivate={deactivatePurposeCode}
        onDelete={deletePurposeCode}
        onStartDrag={onStartDragPurposeCode}
        onEndDrag={onEndDragPurposeCode}
      />
      <ClientServiceAreasDeprecated
        client={client}
        serviceAreas={serviceAreas}
        addServiceArea={addServiceArea}
        onActivate={activateServiceArea}
        onDeactivate={deactivateServiceArea}
        onEdit={editServiceArea}
        onDelete={deleteServiceArea}
        onStartDrag={onStartDragServiceArea}
        onEndDrag={onEndDragServiceArea}
      />
      <ClientTransportationBenefits
        client={client}
        transportationBenefits={transportationBenefits}
        onAdd={addTransportationBenefit}
        onActivate={activateTransportationBenefit}
        onDeactivate={deactivateTransportationBenefit}
        onEdit={editTransportationBenefit}
        onDelete={deleteTransportationBenefit}
        onStartDrag={onStartDragTransportationBenefit}
        onEndDrag={onEndDragTransportationBenefit}
      />
      <ClientServicePlans
        client={client}
        servicePlans={servicePlanList?.servicePlans || []}
        continuationToken={servicePlanList?.continuationToken}
        fetchMoreServicePlans={fetchMoreServicePlans}
        purposeCodes={purposeCodes}
        serviceAreas={serviceAreas}
        transportationBenefits={transportationBenefits}
        servicePlanSelections={servicePlanSelections}
        setServicePlanSelections={setServicePlanSelections}
        onAdd={addServicePlan}
        onEdit={editServicePlan}
        onActivate={activateServicePlan}
        onDeactivate={deactivateServicePlan}
        onDelete={deleteServicePlan}
        onUnassignPurposeCode={unassignPurposeCode}
        onUnassignServiceArea={unassignServiceArea}
        onUnassignTransportationBenefit={unassignTransportationBenefit}
        onStartDrag={onStartDragServicePlan}
        onEndDrag={onEndDragServicePlan}
        onDragEnter={onDragEnterServicePlan}
        onDragLeave={onDragLeaveServicePlan}
        onDrop={onDropOnServicePlan}
        dropServicePlanUUIDs={
          isDraggingAssignable
            ? droppedServicePlans.map((sp) => sp.servicePlanUUID!)
            : []
        }
      />
      <ClientPrograms
        client={client}
        programs={programs}
        servicePlans={servicePlanList?.servicePlans || []}
        purposeCodes={purposeCodes}
        serviceAreas={serviceAreas}
        transportationBenefits={transportationBenefits}
        addProgram={addProgram}
        onEdit={editProgram}
        onActivate={activateProgram}
        onDeactivate={deactivateProgram}
        onDelete={deleteProgram}
        onDragEnter={onDragEnterProgram}
        onDragLeave={onDragLeaveProgram}
        onDrop={onDropOnProgram}
        dropProgramUUID={
          draggedServicePlan && droppedProgram
            ? droppedProgram.programUUID
            : undefined
        }
      />
      {client && (
        <>
          {/* Form dialogs mount on open so their forms and lookup fetches
              don't run on every page load. */}
          {purposeCodeDialogOpen && (
            <PurposeCodeDialog
              clientBC={client.clientBC}
              open={purposeCodeDialogOpen}
              close={() => setPurposeCodeDialogOpen(false)}
            />
          )}
          {serviceAreaDialogOpen && (
            <ServiceAreaDialog
              title={
                editingServiceArea
                  ? t('client.editServiceArea')
                  : t('client.addServiceArea')
              }
              clientBC={client.clientBC}
              serviceArea={editingServiceArea}
              open={serviceAreaDialogOpen}
              close={() => setServiceAreaDialogOpen(false)}
            />
          )}
          {transportationBenefitDialogOpen && (
            <TransportationBenefitDialog
              title={
                editingTransportationBenefit
                  ? t('client.editTransportationBenefit')
                  : t('client.addTransportationBenefit')
              }
              clientBC={client.clientBC}
              transportationBenefit={editingTransportationBenefit}
              open={transportationBenefitDialogOpen}
              close={() => setTransportationBenefitDialogOpen(false)}
            />
          )}
          {servicePlanDialogOpen && (
            <ServicePlanDialog
              title={
                editingServicePlan
                  ? t('client.editServicePlan')
                  : t('client.addServicePlan')
              }
              open={servicePlanDialogOpen}
              client={client}
              programs={programs}
              servicePlan={editingServicePlan}
              close={() => setServicePlanDialogOpen(false)}
            />
          )}
          {programDialogOpen && (
            <ProgramDialog
              title={
                editingProgram
                  ? t('client.editProgram')
                  : t('client.addProgram')
              }
              open={programDialogOpen}
              client={client}
              program={editingProgram}
              close={() => setProgramDialogOpen(false)}
            />
          )}
          <ConfirmationDialog {...confirmDialog.dialogProps} />
          <ActivateDialog {...activateDialog.activateDialogProps} />
        </>
      )}
    </>
  );
}
